import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import GlobeMap, { DOMAINS } from './GlobeMap';
import CountryPanel from './CountryPanel';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

const ESCALATION_COLORS = { peace:'#1a1a2e', economic_competition:'#2d6a4f', clandestine:'#52b788', covert:'#ffcc33', overt:'#ff9933', limited_conventional:'#e63946', lsco:'#c1121f', global_strategic:'#7b2d8b', nuclear_chemical:'#3d0000' };
const ESCALATION_LABELS = { peace:'Peace', economic_competition:'Econ Competition', clandestine:'Clandestine', covert:'Covert', overt:'Overt', limited_conventional:'Ltd Conventional', lsco:'LSCO', global_strategic:'Global Strategic', nuclear_chemical:'Nuclear/Chemical' };
const levelColor = (l) => ESCALATION_COLORS[l] || '#c4a563';
const levelLabel = (l) => ESCALATION_LABELS[l] || (l ? l.charAt(0).toUpperCase()+l.slice(1) : 'Unknown');

// Human-readable region names (no COCOM jargon on buttons)
const REGION_DISPLAY = {
  1: 'Africa',
  2: 'Europe',
  3: 'Indo-Pacific',
  4: 'N. America',
  5: 'S. America',
  6: 'Middle East',
};

const REGION_DOMAIN_ACTIVITY = {
  1: { D2:'Land border incidents in Sahel', D6:'State media influence campaigns' },
  2: { D1:'Baltic airspace incursions', D2:'Eastern flank ground activity', D5:'Russian cyber operations elevated', D6:'Disinformation campaigns active' },
  3: { D1:'Taiwan Strait ADIZ violations', D3:'South China Sea shipping disruption', D6:'PRC information operations' },
  4: { D3:'Arctic shipping route monitoring', D5:'Critical infrastructure targeting' },
  5: { D3:'Caribbean drug interdiction routes', D6:'Influence ops in key elections' },
  6: { D1:'Airspace restrictions active', D3:'Strait of Hormuz elevated', D5:'Iranian cyber campaign', D6:'Regional influence operations' },
};

const DomainModal = ({ domain, domainKey, onClose }) => (
  <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.75)', zIndex:200,
    display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }} onClick={onClose}>
    <div onClick={e => e.stopPropagation()} style={{
      backgroundColor:'#0f1419', border:`2px solid ${domain.color}`,
      borderRadius:'12px', padding:'24px 28px', maxWidth:'480px', width:'100%'
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'14px' }}>
        <div>
          <div style={{ fontSize:'26px', marginBottom:'5px' }}>{domain.icon}</div>
          <div style={{ color:domain.color, fontFamily:'Georgia,serif', fontSize:'18px', fontWeight:'bold' }}>
            {domainKey}: {domain.label}
          </div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:'#666', cursor:'pointer', fontSize:'20px' }}>✕</button>
      </div>
      <p style={{ color:'#8a8b8f', lineHeight:1.7, marginBottom:'16px', fontSize:'13px' }}>{domain.desc}</p>
      <div style={{ padding:'12px 14px', backgroundColor:'rgba(255,255,255,0.03)', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ color:'#c4a563', fontSize:'10px', fontWeight:'bold', letterSpacing:'0.5px', marginBottom:'7px' }}>ACTIVITY TRACKING</div>
        <div style={{ color:'#555', fontSize:'11px', fontStyle:'italic', lineHeight:1.6 }}>
          {domainKey} events will populate as daily briefings are tagged with {domainKey} ({domain.label}) domain indicators.
        </div>
      </div>
    </div>
  </div>
);

const GlobalView = () => {
  const mapRef = useRef(null);
  const [regions, setRegions]                       = useState([]);
  const [loading, setLoading]                       = useState(true);
  const [error, setError]                           = useState(null);
  const [activeRegionId, setActiveRegionId]         = useState(null);
  const [regionDetails, setRegionDetails]           = useState(null);
  const [selectedEntityId, setSelectedEntityId]     = useState(null);
  const [selectedEntityName, setSelectedEntityName] = useState(null);
  const [activeDomains, setActiveDomains]           = useState([]);
  const [modalDomain, setModalDomain]               = useState(null);
  const [referenceEntityId, setReferenceEntityId]   = useState(null);
  const [referenceEntityName, setReferenceEntityName] = useState('');
  const [referenceAssessments, setReferenceAssessments] = useState({});
  const [allEntities, setAllEntities]               = useState([]);

  // Fetch all entities for reference location selector
  useEffect(() => {
    axios.get(`${API_BASE}/api/v2/global/entities`)
      .then(r => setAllEntities(r.data.entities || []))
      .catch(() => {});
  }, []);

  // Fetch bilateral assessments when reference entity changes
  useEffect(() => {
    if (!referenceEntityId) { setReferenceAssessments({}); return; }
    // Fetch all assessments where THIS entity is the TARGET
    // (what threats does the world pose TO the reference entity)
    axios.get(`${API_BASE}/api/escalation-assessments?target_id=${referenceEntityId}`)
      .then(r => {
        const map = {};
        for (const a of (r.data.assessments || [])) {
          const key = a.source_entity_id;
          if (!map[key] || a.escalation_level > map[key].escalation_level) {
            map[key] = a;
          }
        }
        setReferenceAssessments(map);
      })
      .catch(() => setReferenceAssessments({}));
  }, [referenceEntityId]);

  useEffect(() => {
    axios.get(`${API_BASE}/api/v2/global/regions`)
      .then(r => setRegions(r.data.regions || []))
      .catch(() => setError('Failed to load regions'))
      .finally(() => setLoading(false));
  }, []);

  const handleMapEntityClick = useCallback((entityId, entityName) => {
    setSelectedEntityId(entityId);
    setSelectedEntityName(entityName);
  }, []);

  const toggleDomain = (domainKey) => {
    const domain = DOMAINS[domainKey];
    if (!domain.geo) { setModalDomain({ key:domainKey, domain }); return; }
    setActiveDomains(prev =>
      prev.includes(domainKey) ? prev.filter(d => d !== domainKey) : [...prev, domainKey]
    );
  };

  const handleRegionTileClick = async (region) => {
    setSelectedEntityId(null);
    setSelectedEntityName(null);
    if (activeRegionId === region.id) {
      setActiveRegionId(null); setRegionDetails(null);
      mapRef.current?.resetView(); return;
    }
    setActiveRegionId(region.id);
    mapRef.current?.flyToRegion(region.id, parseFloat(region.center_lat), parseFloat(region.center_lon));
    try {
      const r = await axios.get(`${API_BASE}/api/v2/global/regions/${region.id}`);
      setRegionDetails(r.data);
    } catch { setRegionDetails(null); }
  };

  const regionDomainLinks = activeRegionId ? (REGION_DOMAIN_ACTIVITY[activeRegionId] || {}) : {};

  // Show domain icons on domain button only when that domain has activity in active region
  const domainHasActivity = (key) => activeRegionId && regionDomainLinks[key];

  return (
    <div style={{ padding:'clamp(12px, 3vw, 24px)', maxWidth:'1400px', margin:'0 auto' }}>
      <div style={{ background:'rgba(26, 31, 46, 0.6)', border:'1px solid #c4a563', borderRadius:'8px', padding:'2rem' }}>
        <h2 style={{ color:'#c4a563', marginBottom:'16px', fontFamily:"'Garamond', Georgia, serif",
          fontSize:'clamp(22px, 4vw, 32px)' }}>
          Global Bearings Dashboard
        </h2>

      {loading && <div style={{ padding:'40px', textAlign:'center', backgroundColor:'#1a1f2e', borderRadius:'8px', color:'#c4a563' }}>Loading global data...</div>}
      {error   && <div style={{ padding:'16px', backgroundColor:'rgba(255,51,51,0.15)', color:'#ff9999', borderRadius:'8px', marginBottom:'16px' }}>{error}</div>}

      {!loading && !error && (
        <>
          {/* ── Domain toggles ── */}
          <div style={{ marginBottom:'10px' }}>
            <div style={{ color:'#555', fontSize:'10px', fontWeight:'bold', letterSpacing:'0.7px', marginBottom:'7px' }}>
              OPERATIONAL DOMAINS
            </div>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center' }}>
              {Object.entries(DOMAINS).map(([key, domain]) => {
                const isActive = activeDomains.includes(key);
                const hasActivity = domainHasActivity(key);
                // Icon only shows when threshold met (domain active in focused region)
                const showIcon = isActive || hasActivity;
                return (
                  <button key={key} onClick={() => toggleDomain(key)} style={{
                    display:'flex', alignItems:'center', gap:'5px',
                    padding:'5px 10px', borderRadius:'20px', cursor:'pointer',
                    backgroundColor: isActive ? domain.color + '18' : '#1a1f2e',
                    border:`1.5px solid ${isActive ? domain.color : hasActivity ? domain.color+'66' : 'rgba(255,255,255,0.1)'}`,
                    color: isActive ? domain.color : hasActivity ? domain.color+'cc' : '#777',
                    fontSize:'11px', fontWeight:'600', transition:'all 0.15s',
                    fontFamily:"'Inter', sans-serif", letterSpacing:'0.2px', position:'relative'
                  }}>
                    {showIcon && <span style={{ fontSize:'12px' }}>{domain.icon}</span>}
                    <span>{domain.label}</span>
                    {!domain.geo && <span style={{ fontSize:'9px', opacity:0.45 }}>ℹ</span>}
                  </button>
                );
              })}
            </div>
            {activeDomains.length > 0 && (
              <div style={{ marginTop:'6px', color:'#444', fontSize:'11px' }}>
                Active: {activeDomains.map(d => DOMAINS[d].label).join(' · ')}
                <button onClick={() => setActiveDomains([])}
                  style={{ background:'none', border:'none', color:'#c4a563', cursor:'pointer', fontSize:'11px', marginLeft:'8px' }}>
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* ── Reference Location Selector ── */}
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10, flexWrap:'wrap' }}>
            <div style={{ color:'#555', fontSize:'10px', fontWeight:'bold', letterSpacing:'0.7px', flexShrink:0 }}>
              REFERENCE LOCATION
            </div>
            <select
              value={referenceEntityId || ''}
              onChange={e => {
                const val = e.target.value;
                if (!val) { setReferenceEntityId(null); setReferenceEntityName(''); return; }
                const entity = allEntities.find(en => en.id === parseInt(val));
                setReferenceEntityId(parseInt(val));
                setReferenceEntityName(entity?.name || '');
              }}
              style={{ backgroundColor:'#0f1419', border:'1px solid rgba(196,165,99,0.25)',
                color: referenceEntityId ? '#c4a563' : '#555',
                padding:'4px 8px', borderRadius:'5px', fontSize:'11px',
                cursor:'pointer', minWidth:160 }}>
              <option value=''>🌐 Global View (default)</option>
              {allEntities
                .filter(e => ['USA','RUS','CHN','IRN','PRK','UKR','ISR','GBR','FRA','DEU','IND','SAU','TUR'].includes(e.iso_code))
                .map(e => <option key={e.id} value={e.id}>{e.name}</option>)
              }
              <optgroup label='── All Countries ──'>
                {allEntities.map(e => <option key={`all-${e.id}`} value={e.id}>{e.name}</option>)}
              </optgroup>
            </select>
            {referenceEntityId && (
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ color:'#c4a563', fontSize:'11px', fontStyle:'italic' }}>
                  🎯 Threats posed TO {referenceEntityName}
                </span>
                <button onClick={() => { setReferenceEntityId(null); setReferenceEntityName(''); }}
                  style={{ background:'none', border:'1px solid rgba(196,165,99,0.25)',
                    color:'#666', cursor:'pointer', padding:'2px 7px',
                    borderRadius:'4px', fontSize:'10px' }}>✕</button>
              </div>
            )}
          </div>

          {/* ── Map + CountryPanel ── */}
          {/* On mobile: stacked. On desktop: side by side */}
          <div style={{ display:'flex', flexWrap:'wrap', marginBottom:'16px',
            borderRadius:'8px', overflow:'hidden', gap:'0' }}>
            <div style={{ flex:'1 1 300px', minWidth:0 }}>
              <GlobeMap ref={mapRef} onRegionClick={handleMapEntityClick} activeDomains={activeDomains} referenceAssessments={referenceAssessments} referenceEntityId={referenceEntityId} />
            </div>
            {selectedEntityId && (
              <CountryPanel
                entityId={selectedEntityId}
                entityName={selectedEntityName}
                activeDomains={activeDomains}
                onClose={() => { setSelectedEntityId(null); setSelectedEntityName(null); }}
                referenceEntityId={referenceEntityId}
                referenceEntityName={referenceEntityName}
                referenceAssessments={referenceAssessments}
              />
            )}
          </div>

          {/* ── Operational Regions ── */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'7px' }}>
            <div style={{ color:'#555', fontSize:'10px', fontWeight:'bold', letterSpacing:'0.7px' }}>
              OPERATIONAL REGIONS
            </div>
            {activeRegionId && (
              <button onClick={() => { setActiveRegionId(null); setRegionDetails(null); mapRef.current?.resetView(); }}
                style={{ background:'none', border:'1px solid rgba(196,165,99,0.25)', color:'#c4a563',
                  cursor:'pointer', padding:'3px 9px', borderRadius:'5px', fontSize:'10px' }}>
                ↩ Reset
              </button>
            )}
          </div>
          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'16px', alignItems:'center' }}>
            {regions.map((region) => {
              const isActive = activeRegionId === region.id;
              const escColor = levelColor(region.threat_level);
              const displayName = REGION_DISPLAY[region.id] || region.name.replace(/^R\d+: /,'');
              return (
                <button key={region.id} onClick={() => handleRegionTileClick(region)} style={{
                  display:'flex', alignItems:'center', gap:'5px',
                  padding:'5px 10px', borderRadius:'20px', cursor:'pointer',
                  backgroundColor: isActive ? escColor+'18' : '#1a1f2e',
                  border:`1.5px solid ${isActive ? escColor : escColor+'66'}`,
                  color: isActive ? escColor : '#aaa',
                  fontSize:'12px', fontWeight:'bold', transition:'all 0.15s',
                  fontFamily:'inherit', letterSpacing:'0.2px'
                }}>
                  {displayName}
                  {isActive && (
                    <span style={{ width:'6px', height:'6px', borderRadius:'50%',
                      backgroundColor:escColor, display:'inline-block', flexShrink:0 }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Cross-domain activity panel (shows when region active) ── */}
          {activeRegionId && Object.keys(regionDomainLinks).length > 0 && (
            <div style={{ padding:'12px 16px', backgroundColor:'rgba(196,165,99,0.03)',
              borderRadius:'8px', border:'1px solid rgba(196,165,99,0.12)', marginBottom:'14px' }}>
              <div style={{ color:'#c4a563', fontSize:'10px', fontWeight:'bold', letterSpacing:'0.6px', marginBottom:'9px' }}>
                DOMAIN ACTIVITY — {REGION_DISPLAY[activeRegionId]}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'6px' }}>
                {Object.entries(regionDomainLinks).map(([dk, desc]) => {
                  const domain = DOMAINS[dk];
                  const isToggled = activeDomains.includes(dk);
                  return (
                    <div key={dk} style={{ display:'flex', gap:'8px', alignItems:'flex-start',
                      padding:'8px 10px', backgroundColor:'#0f1419',
                      borderRadius:'6px', border:`1px solid ${domain?.color}22` }}>
                      <span style={{ fontSize:'13px', flexShrink:0, lineHeight:1.4 }}>{domain?.icon}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ color:domain?.color, fontSize:'11px', fontWeight:'bold', marginBottom:'2px' }}>
                          {domain?.label}
                        </div>
                        <div style={{ color:'#555', fontSize:'11px', lineHeight:1.4 }}>{desc}</div>
                      </div>
                      {domain?.geo && (
                        <button onClick={() => toggleDomain(dk)} style={{
                          background:'none', border:`1px solid ${isToggled ? domain.color : '#2a2a2a'}`,
                          color: isToggled ? domain.color : '#444',
                          cursor:'pointer', padding:'2px 7px', borderRadius:'4px', fontSize:'10px', flexShrink:0
                        }}>
                          {isToggled ? 'on' : 'off'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Region entity list ── */}
          {regionDetails && activeRegionId && (
            <div style={{ padding:'16px 18px', backgroundColor:'#1a1f2e',
              borderRadius:'8px', border:'1px solid rgba(196,165,99,0.1)', marginBottom:'16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'4px' }}>
                <h3 style={{ color:'#c4a563', margin:0, fontSize:'14px' }}>
                  {REGION_DISPLAY[activeRegionId]} — {regionDetails.region.name.replace(/^R\d+: /,'')}
                </h3>
              </div>
              <p style={{ color:'#555', marginBottom:'12px', fontSize:'12px', lineHeight:1.6 }}>
                {regionDetails.region.description}
              </p>
              {regionDetails.entityCount > 0 && (
                <>
                  <div style={{ color:'#555', fontSize:'10px', fontWeight:'bold', letterSpacing:'0.5px', marginBottom:'8px' }}>
                    ENTITIES IN AOR ({regionDetails.entityCount})
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'6px' }}>
                    {regionDetails.entities.map((e) => (
                      <div key={e.id}
                        onClick={() => { setSelectedEntityId(e.id); setSelectedEntityName(e.name); }}
                        style={{ padding:'9px 11px', backgroundColor:'#0f1419',
                          border:`1px solid ${levelColor(e.threat_level)}44`,
                          borderRadius:'6px', cursor:'pointer', transition:'border-color 0.15s' }}>
                        <div style={{ color:'#e8e9ed', fontWeight:'bold', fontSize:'13px', marginBottom:'2px' }}>{e.name}</div>
                        <div style={{ color:'#444', fontSize:'11px', marginBottom:'5px' }}>
                          {e.iso_code} · {parseInt(e.population).toLocaleString()}
                        </div>
                        <div style={{ display:'inline-block', padding:'1px 6px',
                          backgroundColor:levelColor(e.threat_level)+'1a',
                          color:levelColor(e.threat_level),
                          border:`1px solid ${levelColor(e.threat_level)}55`,
                          borderRadius:'4px', fontSize:'10px', fontWeight:'bold',
                          textTransform:'uppercase', letterSpacing:'0.3px' }}>
                          {levelLabel(e.threat_level)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div style={{ padding:'9px 12px', backgroundColor:'#0f1419', borderRadius:'8px',
            border:'1px solid rgba(196,165,99,0.06)', color:'#333', fontSize:'11px' }}>
            ✅ Bearing Intelligence Platform · {regions.length} regions · {new Date().toLocaleString()}
          </div>
        </>
      )}

      {modalDomain && (
        <DomainModal domain={modalDomain.domain} domainKey={modalDomain.key} onClose={() => setModalDomain(null)} />
      )}
      </div>
    </div>
  );
};

export default GlobalView;
